import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCustomerProfile, loginCustomer, registerCustomer, logoutCustomer } from '../services/api';

const CustomerContext = createContext();

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
};

export const CustomerProvider = ({ children, onLogin, onLogout }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const savedToken = localStorage.getItem('customer_token');
    if (savedToken) {
      try {
        const profile = await getCustomerProfile(savedToken);
        setCustomer(profile);
        setToken(savedToken);
        // Notify cart context about existing login
        if (onLogin) onLogin(savedToken);
      } catch (error) {
        // Invalid token, clear it
        localStorage.removeItem('customer_token');
        setCustomer(null);
        setToken(null);
      }
    }
    setLoading(false);
  };

  const login = async (identifier, password) => {
    const response = await loginCustomer({ identifier, password });
    
    // Check if email verification is required
    if (response.email_verification_required) {
      return response;
    }
    
    localStorage.setItem('customer_token', response.token);
    setToken(response.token);
    setCustomer(response.customer);
    // Notify cart context about login
    if (onLogin) onLogin(response.token);
    return response;
  };

  const register = async (data) => {
    const response = await registerCustomer(data);
    localStorage.setItem('customer_token', response.token);
    setToken(response.token);
    setCustomer(response.customer);
    // Notify cart context about login
    if (onLogin) onLogin(response.token);
    return response;
  };

  const logout = async () => {
    if (token) {
      try {
        await logoutCustomer(token);
      } catch (error) {
        console.error('Error logging out:', error);
      }
    }
    localStorage.removeItem('customer_token');
    setToken(null);
    setCustomer(null);
    // Notify cart context about logout
    if (onLogout) onLogout();
  };

  const refreshCustomer = async () => {
    if (token) {
      try {
        const profile = await getCustomerProfile(token);
        setCustomer(profile);
        return profile;
      } catch (error) {
        console.error('Error refreshing customer:', error);
      }
    }
    return null;
  };

  const isLoggedIn = !!customer && !!token;

  return (
    <CustomerContext.Provider value={{
      customer,
      token,
      loading,
      isLoggedIn,
      login,
      register,
      logout,
      checkAuth,
      refreshCustomer
    }}>
      {children}
    </CustomerContext.Provider>
  );
};

export default CustomerProvider;
